/**
 * Private Rooms Component
 * Create and join invite-only private party rooms
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Lock, Plus, Copy, Users, Check, DoorOpen } from 'lucide-react';

const PrivateRoomsModal = ({ 
  isOpen, 
  onClose, 
  privateRooms = [], 
  onCreateRoom, 
  onJoinRoom, 
  onLeaveRoom,
  currentPrivateRoom,
  userName
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (roomName.trim()) {
      onCreateRoom(roomName.trim());
      setRoomName('');
      setShowCreateForm(false);
    }
  };

  const handleJoin = () => {
    if (joinCode.trim()) {
      onJoinRoom(joinCode.trim().toUpperCase());
      setJoinCode('');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-purple-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-6 h-6 text-purple-400" />
            <h2 className="text-xl font-bold text-white">Private Rooms</h2>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Current Room */}
        {currentPrivateRoom && (
          <div className="mx-4 mt-4 p-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-xl border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{currentPrivateRoom.name}</p>
                <p className="text-white/60 text-xs flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {currentPrivateRoom.playerCount} players
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => copyInviteCode(currentPrivateRoom.code)}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white text-xs"
                >
                  {copiedCode === currentPrivateRoom.code ? (
                    <><Check className="w-3 h-3 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> {currentPrivateRoom.code}</>
                  )}
                </Button>
                <Button
                  onClick={onLeaveRoom}
                  size="sm"
                  className="bg-red-600 hover:bg-red-500 text-xs"
                >
                  Leave
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
          {/* Create Room */}
          {!currentPrivateRoom && (
            <div className="space-y-3">
              {!showCreateForm ? (
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Private Room
                </Button>
              ) : (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="p-4 bg-black/30 rounded-xl border border-purple-500/20 space-y-3"
                >
                  <Input
                    value={roomName}
                    onChange={(e) => setRoomName(e.target.value)}
                    placeholder="Room name..."
                    maxLength={30}
                    className="bg-black/50 border-white/20 text-white"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowCreateForm(false)}
                      variant="outline"
                      className="flex-1 border-white/20 text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreate}
                      disabled={!roomName.trim()}
                      className="flex-1 bg-purple-600 hover:bg-purple-500"
                    >
                      Create
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Join Room */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-gray-900 text-white/40">or join with code</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="Enter invite code..."
                  maxLength={6}
                  className="bg-black/50 border-white/20 text-white uppercase tracking-widest text-center"
                />
                <Button
                  onClick={handleJoin}
                  disabled={joinCode.length < 4}
                  className="bg-green-600 hover:bg-green-500"
                >
                  <DoorOpen className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Active Rooms List */}
          {privateRooms.length > 0 && !currentPrivateRoom && (
            <div className="space-y-2">
              <p className="text-white/60 text-xs uppercase tracking-wider">Your Rooms</p>
              {privateRooms.map((room) => (
                <div
                  key={room.code}
                  className="p-3 bg-black/30 rounded-lg border border-white/10 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-medium text-sm">{room.name}</p>
                    <p className="text-white/50 text-xs">
                      {room.playerCount} players • Created by {room.creatorName}
                    </p>
                  </div>
                  <Button
                    onClick={() => onJoinRoom(room.code)}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-500"
                  >
                    Join
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-purple-500/20 bg-black/30">
          <p className="text-center text-white/40 text-xs">
            Share your invite code with friends to party together
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivateRoomsModal;
